// Single source of truth for how a stock item or a drug alias reads in a medication dropdown.
//
// Every movement modal used to build its own `full_name` string inside the `.then()` of its own
// drug search, which is how the same drug came to read `[In stock: 100] (generic) Name` in one
// modal and `(generic) Name [In stock: 10.00]` in the next. The two formats are fixed here:
//
//     stock item   [In stock: N] (generic) Name          (+ " (Batch: X)" where batches matter)
//     drug alias   (generic) - Name                      (no stock behind it to report)
//
import { applyTallmanLettering } from '@/services/TallmanLettering';
import { sortMedicationList } from '@/services/SortMedicationList';

// Drug names arrive from the database in lower case; only the first letter is raised, so a
// tall-man generic keeps its own capitalisation.
const capitalise = (name) => (name ? name.charAt(0).toUpperCase() + name.slice(1) : '');

/**
 * Formats a stock quantity for display. Quantities are floats, so a whole holding reads as a
 * whole number and a partial pack keeps its decimals - `10` and `10.00` are the same holding
 * and should not look different depending on which modal is open.
 *
 * A quantity that is missing reads as unknown, not as zero. The API can omit it, and the row stays
 * selectable in that case - labelling it `[In stock: 0]` would tell the user the stock is gone.
 */
export function formatStockQty(qty) {
    const value = Number(qty);
    if (!isFinite(value)) return 'unknown';
    return value.toFixed(2).replace(/\.?0+$/, '');
}

/**
 * Decorates `/get-stock-items` (and `/person/{uuid}/stock-register`) rows for a medication
 * dropdown: tall-man lettering, the `full_name` label, the `name` key `sortMedicationList`
 * sorts on, and the `disabled` flag that `optionDisabled="disabled"` reads.
 *
 * `showBatch` appends the batch number, for the warehouse screens that pick stock by batch.
 * `disableZeroStock` is false for requisitions, where requesting stock the source does not yet
 * hold is legitimate - it may be on order.
 */
export function decorateStockItems(items, { showBatch = false, disableZeroStock = true, sort = true } = {}) {
    if (!Array.isArray(items)) return [];

    items.forEach(item => {
        if (item.alias) {
            item.alias.generic_name = item.alias.generic_name ? applyTallmanLettering(item.alias.generic_name) : '';
            item.alias.name = capitalise(item.alias.name);
        }

        // The stock search returns `qty_left` and the patient stock register returns `quantity`.
        // Mirror them so a call site can read either without knowing which endpoint it came from.
        const qty = item.qty_left != null ? item.qty_left : item.quantity;
        item.qty_left = qty;
        item.quantity = qty;

        // A drug with no generic name must not render an empty "()" prefix.
        const genericPrefix = item.alias && item.alias.generic_name ? `(${item.alias.generic_name}) ` : '';
        const name = item.alias ? item.alias.name : 'Unknown';
        const batchSuffix = showBatch ? ` (Batch: ${item.batch_number ?? 'N/A'})` : '';
        item.full_name = `[In stock: ${formatStockQty(qty)}] ${genericPrefix}${name}${batchSuffix}`;

        // sortMedicationList compares on a top level `name`, which on a stock item is nested
        // under `alias`.
        item.name = item.alias ? item.alias.name : '';
        // A missing quantity is not a zero: the API can omit it, and blocking selection then
        // would stop a legitimate movement. Only a real number at or below zero disables a row.
        item.disabled = disableZeroStock && Number(qty) <= 0;
    });

    return sort ? sortMedicationList(items) : items;
}

/**
 * How a batch reads in the batch dropdown beside a stock picker.
 *
 *     A12 — exp 30/11/2026 — 15 left
 *     A12 — no expiry — 15 left
 *     A12 — exp 30/11/2026 — amount unknown
 *
 * The amount comes off the register rather than a stored field, so a batch that has had nothing
 * recorded coming in on it has no amount to show - `tracked` is false and it reads "amount
 * unknown" rather than "0 left", which would claim the stock is gone.
 */
export function batchOptionLabel(batch) {
    if (!batch) return '';

    const expiry = batch.expiry_date ? `exp ${batch.expiry_date}` : 'no expiry';
    // A tracked batch that arrives without a quantity reads the same as an untracked one, rather
    // than "unknown left".
    const trackedAmount = isFinite(Number(batch.quantity)) ? `${formatStockQty(batch.quantity)} left` : 'amount unknown';
    const amount = batch.tracked ? trackedAmount : 'amount unknown';

    return `${batch.batch_name} — ${expiry} — ${amount}`;
}

/**
 * Decorates `/get-drug-aliases` (and `/company-drugs`) rows. These are catalogue entries with
 * no stock behind them, so the label carries no quantity and nothing is disabled.
 */
export function decorateDrugAliases(aliases, { sort = true } = {}) {
    if (!Array.isArray(aliases)) return [];

    aliases.forEach(item => {
        item.generic_name = item.generic_name ? applyTallmanLettering(item.generic_name) : '';
        item.name = capitalise(item.name);
        item.full_name = item.generic_name ? `(${item.generic_name}) \u2013 ${item.name}` : item.name;
    });

    return sort ? sortMedicationList(aliases) : aliases;
}
